import { versionCheckUsecase } from "@/init/initializeInstances";
import { HOME_PATH } from "@/utils/global-constants";
import { useTranslation } from "@/utils/i18n/client";
import { useEffect, useState } from "react";
import { useNavigate, useRouteError } from "react-router";
import Loading from "../loading/loading";
import styles from "./error-boundary.module.css";

interface ErrorFallbackProps {
  error?: Error | null;
  isChunkLoadError?: boolean;
}

function ErrorFallback({ error, isChunkLoadError = false }: ErrorFallbackProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate(HOME_PATH, { replace: true });
  };

  useEffect(() => {
    // Chunk load error인 경우에만 버전 비교 및 캐시 초기화 수행
    if (isChunkLoadError) {
      setIsLoading(true);
      console.log("[ErrorBoundary] Chunk load error 감지 - 버전 조회 시도");

      const checkVersionAndReload = async () => {
        try {
          const versionCheckResult = await versionCheckUsecase.execute();
          if (versionCheckResult.isFailure) {
            console.error("[ErrorBoundary] 버전 조회 실패:", versionCheckResult.error);
            setIsLoading(false);
            return;
          }

          const serverVersion = versionCheckResult.value?.version;
          const currentVersion = __APP_VERSION__;

          console.log("[ErrorBoundary] 버전 비교:", {
            current: currentVersion,
            server: serverVersion,
          });

          // 버전이 다른 경우에만 캐시 초기화 및 새로고침
          if (currentVersion !== serverVersion) {
            console.log("[ErrorBoundary] 버전 차이 감지 - 캐시 초기화 및 새로고침 시작");

            // 모든 브라우저 캐시 삭제
            if ("caches" in window) {
              try {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((name) => caches.delete(name)));
                console.log("[ErrorBoundary] 모든 브라우저 캐시 제거 완료");
              } catch (e) {
                console.warn("[ErrorBoundary] 캐시 삭제 중 오류:", e);
              }
            }

            console.log("[ErrorBoundary] 새로고침 실행");
            window.location.reload();
            return new Promise(() => {
              // 새로고침이 완료될 때까지 렌더링을 중단
              // 이 구문이 없으면 바운더리 에러로 즉시 넘어갔다가, 리로드되어 새로고침됨
            });
          } else {
            console.log("[ErrorBoundary] 버전 동일 - 일반 에러로 처리");
            setIsLoading(false);
          }
        } catch (err) {
          console.error("[ErrorBoundary] 버전 확인 중 예외 발생:", err);
          setIsLoading(false);
        }
      };

      checkVersionAndReload();
    }
  }, [isChunkLoadError]);

  // 로딩 중이면 로딩 화면 표시
  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t("errorBoundary.title")}</h1>
        <p className={styles.message}>{t("errorBoundary.message")}</p>
        {error && process.env.NODE_ENV === "development" && (
          <details className={styles.errorDetails}>
            <summary>{t("errorBoundary.errorDetails", { defaultValue: "에러 상세 정보" })}</summary>
            <pre className={styles.errorStack}>{error.stack}</pre>
          </details>
        )}
        <div className={styles.buttonContainer}>
          <button type="button" className={styles.button} onClick={handleGoHome}>
            {t("errorBoundary.goHome", { defaultValue: "홈으로 이동" })}
          </button>
          <button type="button" className={styles.button} onClick={handleReload}>
            {t("errorBoundary.reload", { defaultValue: "새로고침" })}
          </button>
        </div>
      </div>
    </div>
  );
}

// React Router errorElement용 컴포넌트 (children 불필요)
export function RouterErrorBoundary() {
  const routeError = useRouteError();
  const error = routeError instanceof Error ? routeError : null;

  console.log("[RouterErrorBoundary] 에러 수신:", routeError);
  console.log("[RouterErrorBoundary] 에러 타입:", routeError?.constructor?.name);
  console.log("[RouterErrorBoundary] 에러 메시지:", error?.message);

  // Chunk load error인지 확인
  // 에러가 Error 인스턴스가 아닐 수도 있으므로 안전하게 체크
  let errorMessage = "";
  if (routeError instanceof Error) {
    errorMessage = routeError.message;
  } else if (typeof routeError === "string") {
    errorMessage = routeError;
  } else if (routeError && typeof routeError === "object") {
    const errorObj = routeError as Record<string, unknown>;
    errorMessage = errorObj.message ? String(errorObj.message) : String(routeError);
  } else {
    errorMessage = String(routeError || "");
  }

  const errorString = errorMessage.toLowerCase();
  const isChunkLoadError =
    errorString.includes("failed to fetch dynamically imported module") ||
    errorString.includes("error loading dynamically imported module") || //chrome 오류
    errorString.includes("Unable to preload CSS ") || //firefox 오류
    errorString.includes("TypeError: 'text/html' is not a valid JavaScript MIME type.") || //safari 오류
    (routeError instanceof TypeError && errorString.includes("dynamically imported"));

  console.log("[RouterErrorBoundary] 에러 분석:", {
    errorMessage,
    isChunkLoadError,
    isErrorInstance: error instanceof Error,
    errorType: error?.constructor?.name,
  });

  // Error 인스턴스로 변환하여 전달
  const errorToPass = error instanceof Error ? error : new Error(errorMessage);

  return <ErrorFallback error={errorToPass} isChunkLoadError={isChunkLoadError} />;
}
